import { collection, doc, getDoc, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { toSceneDocument, toSceneNpcPlacement } from './sceneDocumentMapper';
import {
  reconcileSceneDocumentWithLevelDesign,
  resolveSceneLayoutFromLevelDesignId,
} from './sceneLevelDesignReconciliation';
import { sortScenesByRecency } from './scenePersistenceHelpers';
import type { SceneDocument, SceneNpcPlacement } from '../../types/scene';
import { getFirebaseDb } from './client';
import {
  DEFAULT_SCENE_LIMIT,
  SCENES_COLLECTION,
  SCENE_USER_PLACEMENTS_COLLECTION,
  buildSceneUserPlacementsDocId,
  isSeedLinkedSystemScene,
} from './sceneTypes';

import { withRetry } from '../retry';
const reconcileSceneForRead = async (scene: SceneDocument): Promise<SceneDocument> =>
  reconcileSceneDocumentWithLevelDesign(scene, { mode: 'read-only' });

export const reconcileSceneForReadBestEffort = async (
  scene: SceneDocument,
): Promise<SceneDocument> => {
  if (!scene.levelDesignId.trim() || isSeedLinkedSystemScene(scene)) {
    return scene;
  }

  try {
    return await reconcileSceneForRead(scene);
  } catch (error) {
    console.warn(
      '[scenes] Scene reconciliation failed during read; falling back to stored scene.',
      {
        error,
        levelDesignId: scene.levelDesignId,
        sceneId: scene.id,
      },
    );
    return scene;
  }
};

export const mergeSceneNpcPlacements = (
  basePlacements: SceneNpcPlacement[],
  overlayPlacements: SceneNpcPlacement[],
): SceneNpcPlacement[] => {
  const byId = new Map<string, SceneNpcPlacement>();
  basePlacements.forEach((placement) => {
    byId.set(placement.placementId, placement);
  });
  overlayPlacements.forEach((placement) => {
    byId.set(placement.placementId, placement);
  });
  return [...byId.values()];
};

const readSceneUserPlacements = async (
  userId: string,
  sceneId: string,
  worldLayout: SceneDocument['worldLayout'],
): Promise<SceneNpcPlacement[]> => {
  const db = getFirebaseDb();
  const overlaySnapshot = await withRetry(() =>
    getDoc(
      doc(db, SCENE_USER_PLACEMENTS_COLLECTION, buildSceneUserPlacementsDocId(userId, sceneId)),
    ),
  );
  if (!overlaySnapshot.exists()) {
    return [];
  }

  const rawOverlay = overlaySnapshot.data() as Record<string, unknown>;
  const rawPlacements = Array.isArray(rawOverlay.npcPlacements) ? rawOverlay.npcPlacements : [];
  return rawPlacements
    .map((entry, index) => toSceneNpcPlacement(entry, index, worldLayout))
    .filter((entry): entry is SceneNpcPlacement => entry !== null);
};

const mergeUserScenePlacements = async (
  scene: SceneDocument,
  userId?: string,
): Promise<SceneDocument> => {
  const normalizedUserId = userId?.trim();
  if (!normalizedUserId || scene.ownerId === normalizedUserId) {
    return scene;
  }

  const overlayPlacements = await readSceneUserPlacements(
    normalizedUserId,
    scene.id,
    scene.worldLayout,
  );
  if (overlayPlacements.length === 0) {
    return scene;
  }

  return {
    ...scene,
    npcPlacements: mergeSceneNpcPlacements(scene.npcPlacements, overlayPlacements),
  };
};

export const listGalleryScenes = async (userId?: string): Promise<SceneDocument[]> => {
  const db = getFirebaseDb();

  const publishedQuery = query(
    collection(db, SCENES_COLLECTION),
    where('status', 'in', ['published', 'featured']),
    orderBy('publishedAt', 'desc'),
    limit(DEFAULT_SCENE_LIMIT),
  );

  const ownQuery = userId
    ? query(
        collection(db, SCENES_COLLECTION),
        where('ownerId', '==', userId),
        orderBy('updatedAt', 'desc'),
        limit(DEFAULT_SCENE_LIMIT),
      )
    : null;

  const [publishedSnapshot, primaryOwnSnapshot] = await Promise.all([
    withRetry(() => getDocs(publishedQuery)),
    ownQuery ? withRetry(() => getDocs(ownQuery)) : Promise.resolve(null),
  ]);

  let ownSnapshot = primaryOwnSnapshot;
  let ownerQueryMode: 'none' | 'sorted' | 'fallback' = userId ? 'sorted' : 'none';

  if (userId && primaryOwnSnapshot && primaryOwnSnapshot.docs.length === 0) {
    const fallbackOwnQuery = query(
      collection(db, SCENES_COLLECTION),
      where('ownerId', '==', userId),
      limit(DEFAULT_SCENE_LIMIT),
    );
    ownSnapshot = await withRetry(() => getDocs(fallbackOwnQuery));
    ownerQueryMode = 'fallback';
  }

  const toGalleryScene = async (snapshotDoc: {
    id: string;
    data: () => Record<string, unknown>;
  }): Promise<SceneDocument> => {
    const scene = await toSceneDocument(snapshotDoc.id, snapshotDoc.data());
    return reconcileSceneForReadBestEffort(scene);
  };

  const publishedScenes = await Promise.all(
    publishedSnapshot.docs.map((snapshotDoc) => toGalleryScene(snapshotDoc)),
  );
  const ownScenes = ownSnapshot
    ? await Promise.all(ownSnapshot.docs.map((snapshotDoc) => toGalleryScene(snapshotDoc)))
    : [];

  const byId = new Map<string, SceneDocument>();
  publishedScenes.forEach((scene) => {
    byId.set(scene.id, scene);
  });
  ownScenes.forEach((scene) => {
    byId.set(scene.id, scene);
  });

  console.info('[scenes] Loaded gallery scenes', {
    mergedCount: byId.size,
    ownCount: ownSnapshot?.docs.length ?? 0,
    ownerQueryMode,
    publishedCount: publishedSnapshot.docs.length,
    userId: userId ?? null,
  });

  return sortScenesByRecency([...byId.values()]);
};

export const getSceneById = async (
  sceneId: string,
  userId?: string,
): Promise<SceneDocument | null> => {
  const db = getFirebaseDb();
  const snapshot = await withRetry(() => getDoc(doc(db, SCENES_COLLECTION, sceneId)));
  if (!snapshot.exists()) {
    return null;
  }

  const rawScene = snapshot.data() as Record<string, unknown>;
  const levelDesignId = String(rawScene.levelDesignId ?? '').trim();
  const ownerId = String(rawScene.ownerId ?? '').trim();
  const layoutOverride =
    ownerId === 'system' && levelDesignId.startsWith('seed-')
      ? null
      : await resolveSceneLayoutFromLevelDesignId(levelDesignId);
  const scene = await toSceneDocument(snapshot.id, rawScene, layoutOverride ?? undefined);
  const reconciledScene = await reconcileSceneForReadBestEffort(scene);
  return mergeUserScenePlacements(reconciledScene, userId);
};
